import React from "react";
import {
  Home,
  Building2,
  KeyRound,
  Megaphone,
  Users,
  Scale,
  Receipt,
  CheckCircle2,
} from "lucide-react";
import HeaderMenu from "../../component/Navbar/headermenu";
import CustomFooter from "../../component/Footer/customFooter";
import ShowcaseImageLightbox from "../../component/ShowcaseImageLightbox";
import "./about.css";
import taskTable from "../../assets/RS/rs-tasks.png";
import peekView from "../../assets/RS/rs-peekview.png";
import reminders from "../../assets/RS/rs-reminder.png";
import clientDocuments from "../../assets/RS/rs-documents.png";

const features = [
  {
    icon: <Building2 size={26} />,
    title: "Construction & Development",
    description:
      "Material delivery reviews, contractor completion tracking, and project milestones — tracked with client, status, and due date across every development.",
  },
  {
    icon: <KeyRound size={26} />,
    title: "Property Acquisition",
    description:
      "Ownership verification, acquisition opportunities, and land purchase due diligence — organized by client and project, not scattered across spreadsheets.",
  },
  {
    icon: <Megaphone size={26} />,
    title: "Sales & Marketing",
    description:
      "Property site visits, buyer follow-ups, and new project launches tracked as tasks — so no lead goes cold between a showing and a signed agreement.",
  },
  {
    icon: <Users size={26} />,
    title: "Customer Management",
    description:
      "Buyer agreement meetings, pending customer documents, and handover coordination — with clear owners and deadlines for every client interaction.",
  },
];

const showcase = [
  {
    eyebrow: "Every Deal, One Task Board",
    title: "Construction, sales, acquisition, and compliance — tracked in one place",
    description:
      "Construction & Development, Property Acquisition, Sales & Marketing, Customer Management, Legal & Compliance, and Finance & Billing — every deal for MetroLand Group, Pinnacle Properties, Greenfield Developers, Horizon Estates, and UrbanNest Realty tracked with status, period, financial year, and assignee in one board.",
    image: taskTable,
    alt: "Simplifyte task table showing real estate deals across Construction and Development, Property Acquisition, Sales and Marketing, Legal and Compliance, and Finance and Billing with client and status columns",
  },
  {
    eyebrow: "Every Task, With Full Context",
    title: "Open any deal step without losing the bigger picture",
    description:
      "A task like 'Generate booking payment receipt' opens in a side peek panel with its client (Greenfield Developers), financial year, period, due date, and assignee — full context, without leaving the pipeline behind.",
    image: peekView,
    alt: "Simplifyte task peek panel showing Generate booking payment receipt for Greenfield Developers with financial year, due date, and assignee",
  },
  {
    eyebrow: "Reminders",
    title: "Buyer follow-ups, payments, and construction checks — nudged on time",
    description:
      "Overdue and due-today reminders — following up with prospective buyers, reviewing pending property documents, chasing outstanding payments, checking construction progress — so deals don't stall between site visits and closing.",
    image: reminders,
    alt: "Simplifyte Reminders screen showing overdue and pending real estate reminders like follow up with prospective buyer, review pending property documents, and check construction progress",
  },
  {
    eyebrow: "Client Documents",
    title: "Sale agreements, compliance files, and handover docs — organized by project",
    description:
      "GoldenGate Estates, MapleLeaf Properties, Sunrise Homes, Skyline Developers, SilverOak Developers — each project gets its own document folder for sale agreements, legal compliance, construction documents, and property handover, with upload tracking and share history.",
    image: clientDocuments,
    alt: "Simplifyte Client Documents screen showing real estate folders for GoldenGate Estates Construction Documents, MapleLeaf Properties Sale Agreements, Sunrise Homes Customer Agreements, and other project folders",
  },
];

const benefits = [
  "Track every listing, deal stage, and construction milestone in one board",
  "Follow up with buyers and collect documents before deadlines slip",
  "Get overdue nudges for payments, site visits, and legal approvals",
  "Keep sale agreements, compliance files, and handover docs organized by project",
  "Give every agent clarity on their active deals and due dates",
];

const RealEstatePage = () => {
  return (
    <>
      <HeaderMenu />
      <div className="Industry_class_main">
        {/* Hero */}
        <section className="Industry_class_hero">
          <div className="Industry_class_container">
            <div className="Industry_class_hero_content">
              <div className="Industry_class_icon_box">
                <Home size={34} />
              </div>

              <h1 className="Industry_class_heading">
                Deal and listing management software for Real Estate agencies
              </h1>

              <p className="Industry_class_subheading">
                Track listings, buyer follow-ups, construction milestones,
                legal approvals, and deal documents in one place your whole
                team can trust.
              </p>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="Industry_class_features_section">
          <div className="Industry_class_container">
            <h2 className="Industry_class_section_title Industry_class_center">
              Everything a real estate agency's back office needs
            </h2>

            <div className="Industry_class_features_grid">
              {features.map((feature, index) => (
                <div key={index} className="Industry_class_feature_card">
                  <div className="Industry_class_feature_icon">
                    {feature.icon}
                  </div>

                  <h3 className="Industry_class_feature_title">
                    {feature.title}
                  </h3>

                  <p className="Industry_class_feature_description">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Product Showcase */}
        <section className="Industry_class_showcase_section">
          <div className="Industry_class_container">
            <h2 className="Industry_class_section_title Industry_class_center">
              Built for the pace of property deals
            </h2>
            <p className="Industry_class_showcase_subtitle">
              Real screens from the product — deal pipelines, payment tracking,
              reminders, and project documents your team can rely on.
            </p>

            <div className="Industry_class_showcase_list">
              {showcase.map((item, index) => (
                <div
                  key={index}
                  className={`Industry_class_showcase_row ${
                    index % 2 === 1 ? "Industry_class_showcase_row_reverse" : ""
                  }`}
                >
                  <div className="Industry_class_showcase_text">
                    <span className="Industry_class_showcase_eyebrow">
                      {item.eyebrow}
                    </span>
                    <h3 className="Industry_class_showcase_title">
                      {item.title}
                    </h3>
                    <p className="Industry_class_showcase_description">
                      {item.description}
                    </p>
                  </div>

                  <div className="Industry_class_showcase_media">
                    <div className="Industry_class_showcase_frame_outer">
                      <div className="Industry_class_showcase_frame_inner">
                        <ShowcaseImageLightbox
                          src={item.image}
                          alt={item.alt}
                          className="Industry_class_showcase_image"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="Industry_class_benefits_section">
          <div className="Industry_class_container">
            <div className="Industry_class_benefits_wrapper">
              <h2 className="Industry_class_section_title Industry_class_center">
                Why real estate agencies choose Simplifyte
              </h2>

              <div className="Industry_class_benefits_list">
                {benefits.map((benefit, index) => (
                  <div key={index} className="Industry_class_benefit_item">
                    <CheckCircle2
                      size={24}
                      className="Industry_class_check_icon"
                    />

                    <p>{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="Industry_class_cta_section">
          <div className="Industry_class_container">
            <div className="Industry_class_cta_box">
              <h2 className="Industry_class_cta_title">
                Ready to simplify your pipeline?
              </h2>

              <p className="Industry_class_cta_text">
                Join real estate agencies who've brought clarity to listings,
                buyer follow-ups, and deal closings.
              </p>

              <button
                className="Industry_class_cta_button"
                onClick={() =>
                  (window.location.href = "https://app.simplifyte.com")
                }
              >
                Start Free Trial
              </button>
            </div>
          </div>
        </section>
      </div>
      <CustomFooter />
    </>
  );
};

export default RealEstatePage;
